import { chain, isEmpty } from 'lodash';
import { Category, CategoryNaturalKey } from './category';
import { dataMember, typedArray, required } from 'santee-dcts';
import * as moment from 'moment';
import { deserializers, formatters } from '../../core';
import { Site } from '../../sites';
import { Acknowledgement, Readable } from '../../view-history';
import { SubjectNaturalKey } from '../../subjects';

export class VisitReportInfo {
    @dataMember()
    @required({ nullable: true })
    public visit: string;

    @dataMember()
    @required({ nullable: true })
    public accessionNumber: string;

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    @required({ nullable: true })
    public visitCollectionDate: moment.Moment;
}

export class FileLink {
    @dataMember()
    @required({ nullable: true })
    public language: string;

    @dataMember()
    @required({ nullable: true })
    public languageId: string;

    @dataMember()
    @required({ nullable: true })
    public languageCode: string;

    @dataMember()
    @required()
    public fileId: string;

    @dataMember()
    @required({ nullable: true})
    public fileName: string;

    @dataMember()
    @required({ nullable: true})
    public uploadFileName: string;    

    public get fileNameString(): string {
        return this.uploadFileName || '(none)';
    }
}

export class Report implements Readable {
    public category: Category;

    public site: Site | undefined;

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    public postedDateTime: moment.Moment;

    @dataMember()
    public reportId: string;

    @dataMember()
    public isRead: boolean;

    @dataMember()
    public isReadByMe: boolean;

    @dataMember()
    @required()
    public isPreviewAvailable: boolean;

    @dataMember()
    @required()
    public isPrintable: boolean;

    @dataMember()
    @required()
    public isSavable: boolean;

    @dataMember()
    @required({ nullable: true })
    public siteId: number;

    @dataMember()
    @required({ nullable: true })
    public siteNum: string;

    @dataMember()
    @required({ nullable: true })
    public subProtocolId: number;

    @dataMember()
    @required({ nullable: true })
    public protocolId: number;

    @dataMember()
    @required()
    public allSites: boolean;

    @dataMember()
    @typedArray(FileLink)
    public fileLinks: FileLink[];

    @dataMember()
    @typedArray(Site)
    public sites: Site[];

    @dataMember()
    @typedArray(Acknowledgement)
    public viewHistory: Acknowledgement[];

    @dataMember()
    @required({ nullable: true })
    public subcategoryTitle: string;

    @dataMember()
    @required({ nullable: true })
    public subcategoryId: CategoryNaturalKey;

    @dataMember()
    @required({ nullable: true })
    public zcProjectNumber: string;

    @dataMember()
    @required({ nullable: true })
    public region: string;

    @dataMember()
    @required()
    public isHidden: boolean;

    public get fileLinkCategory(): CategoryNaturalKey {
        return this.subcategoryId ||  this.category.categoryId;
    }
}

export abstract class VisitReport extends Report {
    @dataMember()
    @required({ nullable: true })
    public subjectId: number;

    @dataMember()
    @required({ nullable: true })
    public subjectNumber: string;

    @dataMember()
    @required({ nullable: true })
    public subjectInitials: string;

    @dataMember()
    @required({ nullable: true })
    public screeningNumber: string;

    @dataMember()
    @required({ nullable: true })
    public subjectNaturalKey: string;

    public get zcSubjectId(): string | null {
        const result = SubjectNaturalKey.fromString(this.subjectNaturalKey);

        return result
            ? result.zcSubjectId
            : null;
    }

    public get accessionNumbers(): string[] {
        const accessionNumbers = chain(this.visits)
            .filter(x => !isEmpty(x.accessionNumber))
            .map(x => x.accessionNumber)
            .sort()
            .value();
        return accessionNumbers;
    }

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    @required({ nullable: true })
    public subjectBirthDate: moment.Moment;

    @dataMember()
    @required({ nullable: true })
    public subjectGender: string;

    @dataMember()
    @typedArray(VisitReportInfo)
    @required()
    public visits: VisitReportInfo[];
}

export class LabManualsReport extends Report {
    @dataMember()
    public title: string;

    @dataMember()
    @required({ nullable: true })
    public version: string;

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    @required({ nullable: true })
    public versionDate: moment.Moment;

    public getDescription(): string {
        return `Posted at ${formatters.formatDate(this.postedDateTime)}. Title: ${String.fromCharCode(160)}${this.title}`;
    }
}

export class GeneralReport extends Report {
    @dataMember()
    @required()
    public title: string;

    @dataMember()
    @required({ nullable: true })
    public subcategoryTitle: string;

    @dataMember()
    public letterNumber: string;

    @dataMember()
    @typedArray(String)
    @required()
    public audienceRoles: string[];
}

export class SynopsisReport extends Report {
    @dataMember({ customDeserializer: deserializers.deserializeDate })
    public versionDate: moment.Moment;
}

export class LabReport extends VisitReport {
    @dataMember()
    @required({ nullable: true })
    public testGroup: string;

    @dataMember()
    @required({ nullable: true })
    public type: string;

    @dataMember()
    @required({ nullable: true })
    public recipientType: string;

    public get fileLinkCategory(): CategoryNaturalKey {
        switch (this.type) {
            case 'Result Alert':
            case 'Alert':
                return 'result-alerts';
            case 'Data Revision':
                return 'data-revisions';
            case 'Cancellation':
                return 'cancellation';
            default:
                return this.category.categoryId;
        }
    }
}

export class CommunicationReport extends Report {
    @dataMember()
    @required({ nullable: true })
    public title: string;

    @dataMember()
    @required({ nullable: true })
    public letterNumber: string;
}

export class TrainingMaterialsReport extends Report {
    @dataMember()
    @required({ nullable: true })
    public title: string;

    @dataMember()
    @required({ nullable: true })
    public protocolNumber: string;

    @dataMember()
    @required({ nullable: true })
    public allProjects: boolean;

    public get projectNumber(): string {
        return !!this.allProjects ? 'All Projects' : this.zcProjectNumber;
    }
}

export class SiteActionsReport extends VisitReport {
    @dataMember({ customDeserializer: deserializers.deserializeBooleanFlag })
    public isResponseRequired: boolean;

    @dataMember()
    public title: string;

    @dataMember()
    public siteNum: string;

    @dataMember({ customDeserializer: deserializers.deserializeDate })
    public responseNeededBy: moment.Moment;

    public getDescription(): string {
        return `Posted at ${formatters.formatDate(this.postedDateTime)}. Title:${String.fromCharCode(160)}${this.title}`;
    }
}
